import type { SDKResult } from '@saas/api-client';
import type { QueryFunctionContext, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/utils/reactQueryClient';
import { toApiError } from './api-error';
import { AI_QUOTA_EXHAUSTED_CODE, BILLING_USAGE_QUERY_BASE } from './quota-refusal';

type ServiceQueryKey = Array<unknown>;

type QueryOptionsWithOnSuccess<T> = Omit<UseQueryOptions<T, Error, T, ServiceQueryKey>, 'queryKey' | 'queryFn'> & {
	onSuccess?: (data: T) => void;
};

type MutationOptions<TData, TVariables, TContext = unknown> = Omit<
	UseMutationOptions<TData, Error, TVariables, TContext>,
	'mutationFn'
>;

/**
 * Cache policy a service pins to a query definition. It travels with the query options, so a
 * prefetch of the query and the hook that later mounts on it agree on staleness - without that,
 * a boot prefetch lands in cache already stale and the mounting hook immediately refetches it.
 */
export type QueryRecoveryState = {
	state: { status: 'pending' | 'error' | 'success'; data: unknown };
};

export type ServiceQueryDefaults = {
	staleTime?: number;
	gcTime?: number;
	retryOnMount?: boolean;
	refetchInterval?: (query: QueryRecoveryState) => number | false;
};

export type ServiceQueryConfig<TParams, TResponse> = {
	queryFunction: (params: TParams) => Promise<SDKResult<TResponse>>;
	queryKeyFactory: (params: NoInfer<TParams>) => ServiceQueryKey;
	/** Applied under any per-call options the hook (or prefetch) passes. */
	defaultOptions?: ServiceQueryDefaults;
};

/**
 * A service query reduced to what React Query needs outside a component - `queryClient.prefetchQuery`,
 * `ensureQueryData`, route loaders. Built from the same key factory and request function as the hook,
 * so both address the same cache entry.
 */
export type ServiceQueryOptions<TResponse> = ServiceQueryDefaults & {
	queryKey: ServiceQueryKey;
	queryFn: (context: QueryFunctionContext<ServiceQueryKey>) => Promise<TResponse>;
};

export const makeQueryOptions = <TParams, TResponse>({
	queryFunction,
	queryKeyFactory,
	defaultOptions,
}: ServiceQueryConfig<TParams, TResponse>) => {
	return (params: TParams): ServiceQueryOptions<TResponse> => ({
		...defaultOptions,
		queryKey: queryKeyFactory(params),
		queryFn: ({ signal }) =>
			queryFunction({ ...params, signal } as TParams).then((res) => {
				if (res.error) {
					throw toApiError({ error: res.error, fallbackMessage: 'An error occurred while fetching data.' });
				}
				return res.data;
			}),
	});
};

export const makeQuery = <TParams, TResponse>(config: ServiceQueryConfig<TParams, TResponse>) => {
	const buildQueryOptions = makeQueryOptions(config);

	const useServiceQuery = (params: TParams, queryOptions?: QueryOptionsWithOnSuccess<TResponse>) => {
		const { onSuccess, ...restOptions } = queryOptions ?? {};
		const { queryKey, queryFn, ...pinnedOptions } = buildQueryOptions(params);

		return useQuery<TResponse, Error, TResponse, ServiceQueryKey>({
			queryKey,
			queryFn: (context) =>
				queryFn(context).then((data) => {
					onSuccess?.(data);
					return data;
				}),
			...pinnedOptions,
			...restOptions,
		});
	};

	// The hook carries its own options builder, so prefetches and route loaders address the exact
	// cache entry the hook mounts on without the service re-declaring key factory or request fn.
	return Object.assign(useServiceQuery, { queryOptions: buildQueryOptions });
};

export type ServiceMutationConfig<TVariables, TResponse> = {
	mutationFunction: (variables: TVariables) => Promise<SDKResult<TResponse>>;
	onSuccess?: (variables: NoInfer<TVariables>, response: NoInfer<TResponse>) => void;
};

export const makeMutation = <TVariables, TResponse>({
	mutationFunction,
	onSuccess,
}: ServiceMutationConfig<TVariables, TResponse>) => {
	return <TContext = unknown>(options?: MutationOptions<TResponse, TVariables, TContext>) => {
		return useMutation<TResponse, Error, TVariables, TContext>({
			mutationFn: (data) =>
				mutationFunction(data).then((res) => {
					if (res.error) {
						const apiError = toApiError({ error: res.error, fallbackMessage: 'An error occurred.' });
						// A quota refusal means the counter passed the allowance since the last read, so every
						// billing read-out on screen is stale the instant it arrives.
						if (apiError.code === AI_QUOTA_EXHAUSTED_CODE) {
							void queryClient.invalidateQueries({ queryKey: [BILLING_USAGE_QUERY_BASE] });
						}
						throw apiError;
					}
					onSuccess?.(data, res.data);
					return res.data;
				}),
			...options,
		});
	};
};
